import { Text, View } from "react-native";
import { forwardRef, useImperativeHandle } from "react";
import { ModifyResidentRef } from "../interfaces";
import KeyValueSelector from "ui/organisms/KeyValueSelector";
import { Controller } from "react-hook-form";
import { useHandleResidentAdmission } from "../../hooks/useHandleResidentAdmission";
import { AdmissionTypeEnums } from "app/services/api/types/residents/enums/AdmissionTypeEnums";
import { FormControlledInputTextArea } from "ui/molecules/form/FormControlledInputTextArea";
import { ResidentStatusEnum } from "app/services/api/types/residents/enums/ResidentStatusEnum";
import { DependencyEnums } from "app/services/api/types/residents/enums/DependencyEnums";
import UploadDocumentArea from "ui/organisms/UploadDocumentArea";
import { generalAllowedExtensions } from "app/constants/allowedExtensionsConstants";
import {
  requiredValidation,
  validateFileExtensions,
} from "ui/utils/Validators";
import { FormControlledDropdown } from "ui/molecules/form/FormControlledDropdown";
import { ResidentAdmissionForm } from "app/services/api/types/residents/models/ResidentAdmissionForm";
import { FormControlledDatePicker } from "ui/molecules/form/FormControlledDatePicker";
import {
  AttendingForDropdownOptions,
  AttendingForEnum,
} from "app/services/api/types/residents/enums/AttendingForEnum";

const ResidentAdmissionFormTemplate = forwardRef<ModifyResidentRef, {}>(
  (_, ref) => {
    const {
      control,
      admissionTypeOptions,
      statusTypeOptions,
      dependencyTypeOptions,
      submitForm,
      areRoomsLoading,
      areBedsLoading,
      formattedRoomData,
      formattedBedsData,
      attendingFor,
      selectedLocationId,
      selectedRoomId,
      validateRoomSelection,
      validateEndDate,
      setValue,
    } = useHandleResidentAdmission();

    useImperativeHandle(
      ref,
      () => ({
        continue: () => submitForm(),
      }),
      [submitForm],
    );

    return (
      <View>
        <Controller
          control={control}
          name="status"
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <KeyValueSelector<ResidentStatusEnum>
                title="Resident Status"
                options={statusTypeOptions}
                isMultiSelect={false}
                selectedValues={value ? [value] : []}
                onChange={(newValue) => onChange(newValue[0])}
                errorMessage={error}
              />
            );
          }}
          rules={{
            required: requiredValidation("Resident status"),
          }}
        />
        <View className="md:w-10/12 mt-6">
          <FormControlledInputTextArea
            id="status_reason"
            name="status_reason"
            control={control}
            placeholder="Please specify reason"
            className="text-gray-500 "
          />
        </View>
        <View className="border border-gray-200 w-full md:my-8 my-6" />
        <Text className="font-medium mb-2">Allocation</Text>
        <View className="grid md:grid-cols-3 gap-8">
          <FormControlledDatePicker
            id="admission_start_date"
            name="admission_start_date"
            control={control}
            label="Check in"
            placeholder={"00/00/0000"}
            isRequired
          />
          <FormControlledDatePicker
            id="admission_end_date"
            name="admission_end_date"
            control={control}
            label="Check out"
            placeholder={"00/00/0000"}
            rules={{
              validate: {
                requiredIf: validateEndDate,
              },
            }}
          />
          <FormControlledDropdown<ResidentAdmissionForm>
            name="attending_for"
            control={control}
            label="Admission for"
            placeholder="Select admission type"
            isRequired
            options={AttendingForDropdownOptions}
            rules={{
              required: requiredValidation("Admission type"),
            }}
          />
          <FormControlledDropdown<ResidentAdmissionForm>
            name="room_id"
            control={control}
            label="Room allocation"
            placeholder="Select room"
            options={formattedRoomData}
            isDisabled={!selectedLocationId || areRoomsLoading}
            rules={{
              validate: {
                requiredIf: validateRoomSelection,
              },
            }}
            isRequired={attendingFor === AttendingForEnum.RESIDENT}
            canClear
          />
          <FormControlledDropdown<ResidentAdmissionForm>
            name="bed_id"
            control={control}
            label="Bed allocation"
            placeholder="Select bed"
            options={formattedBedsData}
            isDisabled={!selectedRoomId || areBedsLoading}
            canClear
            isRequired={attendingFor === AttendingForEnum.RESIDENT}
            confirmSelectItem
            onConfirmSelectItem={(item) => {
              if (!item.disabled) {
                setValue("bed_id", item.value);
              }
            }}
            rules={{
              validate: {
                requiredIf: (value) => {
                  if (selectedRoomId && !value) {
                    return "Bed selection is required when a room is selected";
                  }
                  return true;
                },
              },
            }}
          />
        </View>
        <View className="border border-gray-200 w-full md:my-16 my-12" />

        <Controller
          control={control}
          name="admission_type"
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <KeyValueSelector<AdmissionTypeEnums>
                title="Admission Type"
                options={admissionTypeOptions}
                isMultiSelect={false}
                selectedValues={value ? [value] : []}
                onChange={(newValue) => onChange(newValue[0])}
                errorMessage={error}
              />
            );
          }}
          rules={{
            required: requiredValidation("Admission Type"),
          }}
        />
        <View className="md:w-10/12 mt-6">
          <FormControlledInputTextArea
            id="admission_type_details"
            name="admission_type_details"
            control={control}
            placeholder="If other, please specify admission type"
            className="text-gray-500 "
          />
        </View>
        <View className="border border-gray-200 w-full md:my-16 my-12" />

        <Controller
          control={control}
          name="dependency"
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <KeyValueSelector<DependencyEnums>
                title="Dependency"
                options={dependencyTypeOptions}
                isMultiSelect={false}
                selectedValues={value ? [value] : []}
                onChange={(newValue) => onChange(newValue[0])}
                errorMessage={error}
              />
            );
          }}
          rules={{
            required: requiredValidation("Dependency"),
          }}
        />
        <View className="border border-gray-200 w-full md:my-16 my-12" />
        <View className="md:w-10/12">
          <Text className="font-medium mb-5">Other Notes</Text>
          <FormControlledInputTextArea
            id="other_notes"
            name="other_notes"
            control={control}
            placeholder="Enter any other important notes"
            className="text-gray-500"
          />
          <Controller
            control={control}
            name="other_notes_media"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <UploadDocumentArea
                value={value}
                onChange={onChange}
                allowedExtensions={generalAllowedExtensions}
                errorMessage={error}
                singleFile
              />
            )}
            rules={{
              validate: validateFileExtensions(generalAllowedExtensions),
            }}
          />
        </View>
      </View>
    );
  },
);

ResidentAdmissionFormTemplate.displayName = "ResidentAdmissionFormTemplate";

export { ResidentAdmissionFormTemplate };
